import { MapBounds } from '../types';
import { getTile } from './tileCache';
import { isUnmetered } from './networkStatus';
import { getTilesForBounds, buildXyzUrl } from './tileDownloaderService';

// Re-eksporter den delte URL-byggeren så eksisterende importer/tester består.
export { buildXyzUrl };

/**
 * Proaktiv wifi-prefetch: henter fliser rundt viewport (og ett zoom-nivå ut)
 * på forhånd, KUN på umålt nett. Dette dekker scenarioet der brukeren glemte å
 * lagre kart før hen forlot wifi — kanten av det de så på er allerede i cachen.
 *
 * Stille (ingen dialog), bundet (maks antall fliser), og avbrytbar på neste
 * kartflytt via AbortSignal.
 */

/** Maks fliser per prefetch-runde, så vi ikke hamrer flis-serverne. */
export const DEFAULT_PREFETCH_MAX_TILES = 80;

/** Utvider bounds med én fliss-bredde på hver kant (grov approksimasjon). */
function expandBounds(bounds: MapBounds, factor: number): MapBounds {
  const latPad = (bounds.latMax - bounds.latMin) * factor;
  const lonPad = (bounds.lonMax - bounds.lonMin) * factor;
  return {
    latMin: bounds.latMin - latPad,
    latMax: bounds.latMax + latPad,
    lonMin: bounds.lonMin - lonPad,
    lonMax: bounds.lonMax + lonPad,
  };
}

/**
 * Flytter et bounds `factor` viewport-lengder i kjøreretningen (dLat/dLon).
 * Brukes til å prefetche en korridor FORAN deg under auto-nav i stedet for en
 * symmetrisk ring — fliser du er på vei mot blir varme før de ruller inn.
 * Retningen normaliseres; magnitude ignoreres (kun retningen brukes).
 */
export function shiftBoundsForward(
  bounds: MapBounds,
  dLat: number,
  dLon: number,
  factor: number,
): MapBounds {
  const mag = Math.hypot(dLat, dLon);
  if (mag === 0) return bounds;
  const uLat = dLat / mag;
  const uLon = dLon / mag;
  const shiftLat = uLat * (bounds.latMax - bounds.latMin) * factor;
  const shiftLon = uLon * (bounds.lonMax - bounds.lonMin) * factor;
  return {
    latMin: bounds.latMin + shiftLat,
    latMax: bounds.latMax + shiftLat,
    lonMin: bounds.lonMin + shiftLon,
    lonMax: bounds.lonMax + shiftLon,
  };
}

export interface PrefetchResult {
  skipped: boolean;
  reason?: 'metered' | 'aborted' | 'no-templates';
  fetched: number;
}

/**
 * Prefetch fliser rundt (og litt utenfor) viewport for de gitte lag-malene.
 * Deduper URL-er, respekterer maks-tak, og hopper stille over på målt nett.
 */
export async function prefetchAround(
  urlTemplates: string[],
  bounds: MapBounds,
  zoom: number,
  signal?: AbortSignal,
  opts?: {
    maxTiles?: number;
    isUnmeteredFn?: () => boolean;
    getTileFn?: typeof getTile;
    /** Kjøreretning (senter-delta). Når satt → prefetch en korridor FORAN i stedet for symmetrisk ring. */
    direction?: { dLat: number; dLon: number };
    /** Tillat prefetch selv på målt nett (kun under auto-nav, med stramt tak). */
    allowMetered?: boolean;
  },
): Promise<PrefetchResult> {
  const maxTiles = opts?.maxTiles ?? DEFAULT_PREFETCH_MAX_TILES;
  const unmetered = opts?.isUnmeteredFn ?? isUnmetered;
  const fetchTile = opts?.getTileFn ?? getTile;

  if (urlTemplates.length === 0) return { skipped: true, reason: 'no-templates', fetched: 0 };
  // Målt nett hoppes over — MEN auto-nav får prefetche en liten korridor foran (allowMetered).
  if (!unmetered() && !opts?.allowMetered) return { skipped: true, reason: 'metered', fetched: 0 };
  if (signal?.aborted) return { skipped: true, reason: 'aborted', fetched: 0 };

  const z = Math.round(zoom);
  const dir = opts?.direction;
  const hasDir = !!dir && Math.hypot(dir.dLat, dir.dLon) > 0;

  const urls = new Set<string>();
  for (const template of urlTemplates) {
    if (hasDir) {
      // Korridor i kjøreretningen (samme zoom, ingen zoom-ut → sparer data på målt nett):
      // liten ring rundt deg + skjøvet 1 og 2 viewport-lengder fremover.
      for (const region of [
        expandBounds(bounds, 0.3),
        shiftBoundsForward(bounds, dir!.dLat, dir!.dLon, 1),
        shiftBoundsForward(bounds, dir!.dLat, dir!.dLon, 2),
      ]) {
        for (const t of getTilesForBounds(region, z)) {
          urls.add(buildXyzUrl(template, t.z, t.x, t.y));
        }
      }
    } else {
      // Symmetrisk ring rundt viewport på nåværende zoom + hele viewport ett nivå ut.
      for (const t of getTilesForBounds(expandBounds(bounds, 0.5), z)) {
        urls.add(buildXyzUrl(template, t.z, t.x, t.y));
      }
      if (z > 1) {
        for (const t of getTilesForBounds(bounds, z - 1)) {
          urls.add(buildXyzUrl(template, t.z, t.x, t.y));
        }
      }
    }
  }

  let fetched = 0;
  for (const url of urls) {
    if (signal?.aborted) return { skipped: false, reason: 'aborted', fetched };
    if (fetched >= maxTiles) break;
    try {
      // getTile er cache-first, så allerede-cachede fliser koster ingenting.
      await fetchTile(url, { signal });
      fetched++;
    } catch {
      // Enkeltfeil (nett/abort) er ikke kritisk for prefetch.
    }
  }

  return { skipped: false, fetched };
}
